import { environment } from '@symbo.ls/sdk'
import { workspaceApiBase } from '../functions/workspaceApiBase.js'
import { consumeOAuthNext } from '../functions/postOAuthNext.js'
import { readInviteToken, clearInviteToken } from '../functions/inviteToken.js'
import { NATIVE_AUTH_SCHEMES, nativeShellScheme } from '../functions/isNativeShell.js'

const PLUGIN_SESSION_STORAGE_KEY = 'plugin_auth_session'

const ensurePluginSession = () => {
  if (typeof window === 'undefined') {
    return null
  }

  let session = null

  try {
    session = new URL(window.location.href).searchParams.get('session')
  } catch {
    // Ignore URL parsing errors
  }

  if (session) {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(PLUGIN_SESSION_STORAGE_KEY, session)
      }
    } catch {
      // Ignore storage errors
    }
    return session
  }

  try {
    if (window.localStorage) {
      return window.localStorage.getItem(PLUGIN_SESSION_STORAGE_KEY)
    }
  } catch {
    // Ignore storage errors
  }

  return null
}

export async function handleGoogleAuthResponse(credential) {
  const state = this.getRootState()
  try {
    // Get invite token from localStorage instead of URL
    const inviteToken = localStorage.getItem('google_auth_invite_token')

    const pluginSession = ensurePluginSession()
    const loginResult = pluginSession
      ? await this.sdk.googleAuth(credential, inviteToken, {
          session: pluginSession
        })
      : await this.sdk.googleAuth(credential, inviteToken)

    if (!loginResult) {
      throw new Error('Invalid response from server')
    }

    // Clean up the stored token
    localStorage.removeItem('google_auth_invite_token')
    // A sign-in on this origin: its refresh token is its own (review D1).
    this.call('markOwnSession')

    this.call('applyLoader', {
      value: true,
      force: true,
      timeout: 1
    })

    await this.call(
      'initializeUserSession',
      {
        loginData: loginResult
      },
      this.getRootState(),
      this.sdk
    )
    state.update({ loading: false })

    // SIGNUP-INTENT — see functions/signupIntent.js
    this.router(this.call('takeSignupCheckoutNext') || '/')
  } catch (error) {
    console.error('Failed to sign in with Google:', error)
    // Clean up the stored token on error
    localStorage.removeItem('google_auth_invite_token')
    this.call('openNotification', {
      title: this.call('polyglot', 'auth.signin.failed'),
      message: error.message,
      type: 'error'
    })
    state.update({ loading: false })
  }
}

// Check for OAuth callback on component render
export function checkGoogleOAuthCallback() {
  const state = this.getRootState()
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')
  const stateParam = urlParams.get('state')
  const error = urlParams.get('error')
  const storedState = sessionStorage.getItem('google_oauth_state')

  if (error) {
    console.error('Google OAuth error:', error)
    this.call('openNotification', {
      title: this.call('polyglot', 'auth.google.authFailed.title'),
      message: this.call('polyglot', 'auth.google.authFailed.message'),
      type: 'error'
    })
    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname)
    return
  }

  if (code && stateParam) {
    // Verify state parameter
    if (stateParam !== storedState) {
      console.error('State parameter mismatch - possible CSRF attack')
      this.call('openNotification', {
        title: this.call('polyglot', 'auth.google.securityError.title'),
        message: this.call('polyglot', 'auth.google.securityError.message'),
        type: 'error'
      })
      window.history.replaceState({}, document.title, window.location.pathname)
      return
    }

    // Clean up stored state
    sessionStorage.removeItem('google_oauth_state')

    // Clean up URL first
    window.history.replaceState({}, document.title, window.location.pathname)

    // Process the OAuth code
    this.call('processGoogleOAuthCode', code, this, state)
  }
}

// Helper method to handle redirect-based Google OAuth flow.
//
// Optional overrides (all default to the historic web behavior, so plain
// `handleGoogleRedirectFlow()` is byte-identical to before):
//   redirectUri — where Google sends the code (default: the SPA's
//     /google-auth-callback page; the native shell points it at the SERVER
//     callback /auth/google/native-callback instead — tickets/ipad.md)
//   state — the OAuth `state` value (default: a throwaway web nonce; the
//     native shell passes its deep-link binding nonce)
//   open — how to leave for Google (default: top-level navigation; the
//     native shell hands the URL to the system browser)
export function handleGoogleRedirectFlow({ redirectUri, state, open } = {}) {
  try {
    // Store current URL to return to after auth
    sessionStorage.setItem('google_auth_return_url', window.location.href)

    ensurePluginSession()

    // Store invite token in localStorage instead of appending to redirect URI
    const currentUrl = new URL(window.location.href)
    const inviteToken = currentUrl.searchParams.get('token')
    if (inviteToken) {
      localStorage.setItem('google_auth_invite_token', inviteToken)
    }

    // Create the Google OAuth URL with clean redirect URI (no dynamic parameters)
    const clientId = environment.googleClientId
    const finalRedirectUri = redirectUri || `${window.location.origin}/google-auth-callback`

    const encodedRedirectUri = encodeURIComponent(finalRedirectUri)
    const scope = encodeURIComponent('openid email profile')
    const responseType = 'code'
    const oauthState = state || `google_oauth_${Math.random().toString(36).substr(2, 9)}`

    // Store state to verify later
    sessionStorage.setItem('google_oauth_state', oauthState)

    const googleAuthUrl =
      `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodedRedirectUri}&` +
      `response_type=${responseType}&` +
      `scope=${scope}&` +
      `state=${encodeURIComponent(oauthState)}&` +
      `access_type=offline&` +
      `prompt=consent`

    if (open) {
      // Caller owns the hand-off (native shell → system browser)
      open(googleAuthUrl)
    } else {
      // Redirect to Google OAuth
      window.location.href = googleAuthUrl
    }
  } catch (error) {
    console.error('Failed to initiate Google redirect flow:', error)
    // Clean up stored token on error
    localStorage.removeItem('google_auth_invite_token')
    this.call('openNotification', {
      title: this.call('polyglot', 'auth.signin.failed'),
      message: this.call('polyglot', 'auth.google.unreachable'),
      type: 'error'
    })
  }
}

// ── Native-shell OAuth (Tauri iOS shell — tickets/ipad.md) ────────────────
//
// In the native shell (isNativeShell()) NO in-webview Google flow can work:
// FedCM/One Tap is unavailable in WKWebView, the shell drops the GIS
// code-client popup, and Google rejects OAuth from embedded webviews
// outright ("disallowed_useragent"). The real flow runs in the SYSTEM
// browser with a deep link back:
//
//   1. launchNativeGoogleAuth() reuses the handleGoogleRedirectFlow URL
//      builder with redirect_uri = the SERVER callback
//      (`${workspaceApiBase()}/auth/google/native-callback`) and
//      state = `<nonce>.<scheme>` — a shell nonce persisted in
//      localStorage, plus THIS build's deep-link scheme
//      (nativeShellScheme() — `symbols` prod / `symbols-canary` canary,
//      see isNativeShell.js), then hands the URL to the system browser
//      (openInSystemBrowser). Google returns `state` verbatim, so no new
//      wire parameter is needed for the scheme to survive the Google leg.
//   2. Safari completes OAuth; the server splits `state` back into
//      nonce + scheme (validated against a hard allowlist — an unknown
//      scheme falls back to `symbols`, never echoes attacker-controlled
//      input into the redirect target), mints a ONE-TIME ≤60s exchange
//      token bound to the nonce, and 302s to
//      `<scheme>://auth-callback?xt=…&state=<nonce>` — the SAME scheme
//      the calling build registered, so prod and canary each land back in
//      the app that started them, installed side by side.
//   3. The shell delivers the deep link back into the webview over three
//      possible paths — a 'symbols:auth-callback' window CustomEvent
//      (warm), window.__SYMBOLS_PENDING_AUTH_CALLBACK__ (event fired
//      before the listener armed), or ?xt=…&state=… boot params on /login
//      (cold start). Whichever fires first wins (window._nativeXtHandled).
//   4. completeNativeAuthExchange verifies the nonce (CSRF check, same
//      posture as checkGoogleOAuthCallback), redeems via
//      sdk.exchangeNativeAuthToken(xt, nonce, inviteToken), then
//      initializeUserSession → post-OAuth `next` routing — identical
//      session semantics to the web Google paths.

const NATIVE_NONCE_KEY = 'native_auth_nonce'
const NATIVE_AUTH_EVENT = 'symbols:auth-callback'

const randomNativeNonce = () => {
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

// Shell opener contract — in priority order:
//   1. window.__SYMBOLS_SHELL__.openExternal(url) — explicit bridge, when
//      the shell injects one.
//   2. Tauri opener plugin IPC — granted to my.symbols.app pages via the
//      shell's remote capability (workspace/native/ipad capabilities).
//   3. window.open(_blank) — last resort; the shell maps popup opens to
//      the system browser.
const openInSystemBrowser = (url) => {
  const bridge = window.__SYMBOLS_SHELL__
  if (typeof bridge?.openExternal === 'function') {
    bridge.openExternal(url)
    return
  }
  const invoke = window.__TAURI_INTERNALS__?.invoke
  if (typeof invoke === 'function') {
    Promise.resolve(invoke('plugin:opener|open_url', { url })).catch(() => {
      window.open(url, '_blank')
    })
    return
  }
  window.open(url, '_blank')
}

const parseAuthCallbackUrl = (raw) => {
  try {
    const url = new URL(raw)
    // Accept any REGISTERED build scheme, not just prod's `symbols:` — the
    // canary shell only ever receives links for the scheme it itself
    // registered (`symbols-canary:`), so this is additive, not a widened
    // trust boundary.
    if (!NATIVE_AUTH_SCHEMES.some((scheme) => url.protocol === `${scheme}:`)) return null
    const p = url.searchParams
    return { xt: p.get('xt'), state: p.get('state'), error: p.get('error') }
  } catch (_) {
    return null
  }
}

const parseAuthCallbackPayload = (detail) => {
  if (!detail) return null
  if (typeof detail === 'string') return parseAuthCallbackUrl(detail)
  if (detail.xt || detail.error) {
    return {
      xt: detail.xt || null,
      state: detail.state || null,
      error: detail.error || null
    }
  }
  if (detail.url) return parseAuthCallbackUrl(detail.url)
  return null
}

async function completeNativeAuthExchange(el, payload) {
  if (!payload || (!payload.xt && !payload.error)) return
  // Whichever delivery path (event / pending global / boot param) fires
  // first wins — the exchange token is single-use server-side anyway.
  if (window._nativeXtHandled) return
  window._nativeXtHandled = true
  const s = el.state

  if (payload.error) {
    window._nativeXtHandled = false
    clearInviteToken('google')
    s.update({ loading: false })
    if (payload.error !== 'access_denied') {
      el.call('openNotification', {
        title: el.call('polyglot', 'auth.login.googleSignInFailed'),
        message: el.call('polyglot', 'auth.google.incomplete').replace('{error}', payload.error),
        type: 'error'
      })
    }
    return
  }

  let storedNonce = null
  try {
    storedNonce = window.localStorage.getItem(NATIVE_NONCE_KEY)
    window.localStorage.removeItem(NATIVE_NONCE_KEY)
  } catch (_) {}

  // CSRF check — the deep link must answer the flow THIS app instance
  // started (same posture as checkGoogleOAuthCallback's state verify).
  // The server additionally verifies the nonce against the token record.
  if (!storedNonce || (payload.state && payload.state !== storedNonce)) {
    window._nativeXtHandled = false
    clearInviteToken('google')
    s.update({ loading: false })
    el.call('openNotification', {
      title: el.call('polyglot', 'auth.google.stateMismatch.title'),
      message: el.call('polyglot', 'auth.google.stateMismatch.message'),
      type: 'error'
    })
    return
  }

  s.update({ loading: true })
  const inviteToken = readInviteToken('google')
  try {
    const loginResult = await el.sdk.exchangeNativeAuthToken(payload.xt, storedNonce, inviteToken)
    if (!loginResult) throw new Error('Invalid authentication response')
    await el.call('initializeUserSession', { loginData: loginResult })
    let next = consumeOAuthNext(new URL(window.location.href).searchParams.get('next'))
    // SIGNUP-INTENT — see functions/signupIntent.js
    if (next === '/') next = el.call('takeSignupCheckoutNext') || next
    s.update({ loading: false })
    el.router(next, el.getRoot())
  } catch (error) {
    console.error('[native-auth] exchange failed:', error)
    window._nativeXtHandled = false
    clearInviteToken('google')
    s.update({ loading: false })
    el.call('openNotification', {
      title: el.call('polyglot', 'auth.login.googleFailed'),
      message: error?.message || el.call('polyglot', 'auth.login.tryAgain'),
      type: 'error'
    })
  }
}

// Arm the deep-link entry paths. Idempotent — the window listener installs
// once per page; the pending-global and boot-param checks run every call so
// a re-render can't miss a delivery that raced the first arm.
export function armNativeAuthDeepLink() {
  const el = this
  if (typeof window === 'undefined') return

  if (!window._nativeAuthDeepLinkArmed) {
    window._nativeAuthDeepLinkArmed = true
    window.addEventListener(NATIVE_AUTH_EVENT, (ev) => {
      completeNativeAuthExchange(el, parseAuthCallbackPayload(ev?.detail))
    })
  }

  // Delivery race: the shell dispatched before the listener armed.
  if (window.__SYMBOLS_PENDING_AUTH_CALLBACK__) {
    const pending = window.__SYMBOLS_PENDING_AUTH_CALLBACK__
    delete window.__SYMBOLS_PENDING_AUTH_CALLBACK__
    completeNativeAuthExchange(el, parseAuthCallbackPayload(pending))
  }

  // Cold start: the shell relaunched the webview on /login?xt=…&state=….
  try {
    const params = new URL(window.location.href).searchParams
    if (params.get('xt') || params.get('error')) {
      const payload = {
        xt: params.get('xt'),
        state: params.get('state'),
        error: params.get('error')
      }
      // Strip the one-time token out of the URL/history before redeeming.
      window.history.replaceState({}, document.title, window.location.pathname)
      completeNativeAuthExchange(el, payload)
    }
  } catch (_) {}
}

// Feature-detect the server side of the flow ONCE per app boot, cached on
// el.scope. A probe token is never redeemable, so a 400/401 answer proves
// the exchange route exists with zero side effects; 404 (or network
// failure) keeps the shipped disabled-button guard as the fallback.
// Reflects the verdict into state (`nativeAuthReady`) so the button's
// reactive disabled/cursor/opacity factories repaint.
export async function ensureNativeGoogleAuth() {
  const el = this
  if (typeof window === 'undefined') return

  armNativeAuthDeepLink.call(el)

  const scope = el.scope || {}
  if (scope._nativeAuthReadyProbe === undefined) {
    scope._nativeAuthReadyProbe = (async () => {
      try {
        await el.sdk.exchangeNativeAuthToken('availability-probe', 'availability-probe')
        return true
      } catch (error) {
        const status = error?.status ?? error?.cause?.status
        return status === 400 || status === 401
      }
    })()
  }
  const ready = await scope._nativeAuthReadyProbe
  if (el.state?.nativeAuthReady !== ready) {
    el.state.update({ nativeAuthReady: ready })
  }
}

// Click path for the shell's Google button: mint + persist the deep-link
// binding nonce, then reuse the handleGoogleRedirectFlow URL builder with
// the SERVER callback as redirect_uri and hand the URL to the system
// browser. Invite token + post-OAuth `next` are stashed by the button's
// onClick (same as the web path) and consumed at exchange time.
//
// `state` carries `<nonce>.<scheme>` — the scheme is THIS build's own
// deep-link registration (nativeShellScheme()), never a literal, so the
// server knows which app to bounce back into without prod/canary needing
// to agree on one shared scheme (they can't — iOS leaves it undefined
// which of two installed apps wins a shared scheme). Only the bare nonce
// (stored above) is ever compared client-side — completeNativeAuthExchange
// never sees or needs the scheme suffix.
export function launchNativeGoogleAuth() {
  const nonce = randomNativeNonce()
  try {
    window.localStorage.setItem(NATIVE_NONCE_KEY, nonce)
  } catch (_) {}
  window._nativeXtHandled = false
  handleGoogleRedirectFlow.call(this, {
    redirectUri: `${workspaceApiBase()}/auth/google/native-callback`,
    state: `${nonce}.${nativeShellScheme()}`,
    open: openInSystemBrowser
  })
}

// Process the OAuth authorization code
export async function processGoogleOAuthCode(code) {
  const state = this.getRootState()
  try {
    state.update({ loading: true })

    // Get invite token from localStorage instead of URL
    const inviteToken = localStorage.getItem('google_auth_invite_token')

    // Use clean redirect URI (no dynamic parameters)
    const redirectUri = `${window.location.origin}/google-auth-callback`

    const pluginSession = ensurePluginSession()

    const loginResult = pluginSession
      ? await this.sdk.googleAuthCallback(code, redirectUri, inviteToken, {
          session: pluginSession
        })
      : await this.sdk.googleAuthCallback(code, redirectUri, inviteToken)

    if (!loginResult) {
      throw new Error('Invalid authentication response')
    }

    // Clean up the stored token
    localStorage.removeItem('google_auth_invite_token')
    // A sign-in on this origin: its refresh token is its own (review D1).
    this.call('markOwnSession')

    await this.call(
      'initializeUserSession',
      {
        loginData: loginResult
      },
      this.getRootState(),
      this.sdk
    )

    state.update({ loading: false })

    // SIGNUP-INTENT — see functions/signupIntent.js
    this.router(this.call('takeSignupCheckoutNext') || '/')
  } catch (error) {
    console.error('Failed to process Google OAuth code:', error)
    // Clean up stored token on error
    localStorage.removeItem('google_auth_invite_token')
    this.call('openNotification', {
      title: this.call('polyglot', 'auth.google.authFailed.title'),
      message: error.message || this.call('polyglot', 'auth.google.incompleteGeneric'),
      type: 'error'
    })
    state.update({ loading: false })
  }
}
